import {
  Button,
  Card,
  DataTable,
  EmptyState,
  Layout,
  Modal,
  Page,
  Spinner,
  Text,
  TextField,
  Toast,
} from '@shopify/polaris'
import { useCallback, useEffect, useState } from 'react'

import FormattedDateTime from '../components/FormattedDateTime'
import { useAdminApi } from '../hooks/useAdminApi'

interface TestStore {
  myshopify_domain: string
  note: string
  created_by: string
  created_at: string
}

// Shops on the allowlist subscribe to billing plans in `test:true` mode (no
// charge) for QA — see docs/pricing.md §8 and §12.
const DOMAIN_PATTERN = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/

function normalizeDomain(value: string): string {
  return value.trim().toLowerCase()
}

export default function AdminTestStoresPage() {
  const { listTestStores, addTestStore, removeTestStore } = useAdminApi()

  const [testStores, setTestStores] = useState<TestStore[]>([])
  const [loading, setLoading] = useState(true)
  const [toastMessage, setToastMessage] = useState('')
  const [toastError, setToastError] = useState(false)

  // Add modal
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [domainInput, setDomainInput] = useState('')
  const [noteInput, setNoteInput] = useState('')
  const [domainError, setDomainError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Remove confirmation
  const [removeTarget, setRemoveTarget] = useState<TestStore | null>(null)
  const [removing, setRemoving] = useState(false)

  const showToast = (message: string, isError = false) => {
    setToastMessage(message)
    setToastError(isError)
  }

  const fetchTestStores = useCallback(async () => {
    setLoading(true)
    const response = await listTestStores()
    if (response.success) {
      // Accept either a bare array (`data: TestStore[]`) or a wrapped shape
      // (`data: { test_stores: TestStore[] }`) so the UI is robust to whichever
      // the backend returns.
      const data = response.data
      const list: TestStore[] = Array.isArray(data)
        ? data
        : (data?.test_stores ?? [])
      setTestStores(list)
    } else {
      showToast(response.error || 'Failed to load test stores', true)
    }
    setLoading(false)
  }, [listTestStores])

  useEffect(() => {
    fetchTestStores()
  }, [fetchTestStores])

  const openAddModal = () => {
    setDomainInput('')
    setNoteInput('')
    setDomainError('')
    setAddModalOpen(true)
  }

  const closeAddModal = () => {
    if (submitting) return
    setAddModalOpen(false)
  }

  const handleDomainChange = (value: string) => {
    setDomainInput(value)
    if (domainError) setDomainError('')
  }

  const handleAddSubmit = async () => {
    const domain = normalizeDomain(domainInput)

    if (!domain) {
      setDomainError('Domain is required')
      return
    }
    if (!DOMAIN_PATTERN.test(domain)) {
      setDomainError(
        'Enter a valid myshopify.com domain, e.g. my-store.myshopify.com'
      )
      return
    }

    setSubmitting(true)
    const response = await addTestStore(domain, noteInput.trim())
    setSubmitting(false)

    if (response.success) {
      showToast(`Added ${domain} to the test-store allowlist`)
      setAddModalOpen(false)
      fetchTestStores()
    } else {
      showToast(response.error || 'Failed to add test store', true)
    }
  }

  const handleRemoveConfirm = async () => {
    if (!removeTarget) return

    setRemoving(true)
    const response = await removeTestStore(removeTarget.myshopify_domain)
    setRemoving(false)

    if (response.success) {
      showToast(`Removed ${removeTarget.myshopify_domain} from the allowlist`)
      setRemoveTarget(null)
      fetchTestStores()
    } else {
      showToast(response.error || 'Failed to remove test store', true)
    }
  }

  const rows = testStores.map(store => [
    <Text as="span" variant="bodyMd" fontWeight="semibold">
      {store.myshopify_domain}
    </Text>,
    store.note || '-',
    store.created_by || '-',
    <FormattedDateTime date={store.created_at} />,
    <Button size="micro" tone="critical" onClick={() => setRemoveTarget(store)}>
      Remove
    </Button>,
  ])

  const toast = toastMessage ? (
    <Toast
      content={toastMessage}
      error={toastError}
      onDismiss={() => setToastMessage('')}
    />
  ) : null

  return (
    <>
      {toast}
      <Page
        title="Test stores"
        subtitle="Real stores on this allowlist subscribe in test mode (no charge) for QA."
        primaryAction={{
          content: 'Add test store',
          onAction: openAddModal,
        }}
      >
        <Layout>
          <Layout.Section>
            <Card>
              {loading ? (
                <div style={{ padding: '32px', textAlign: 'center' }}>
                  <Spinner size="large" />
                </div>
              ) : testStores.length === 0 ? (
                <EmptyState
                  heading="No test stores yet"
                  image=""
                  action={{
                    content: 'Add test store',
                    onAction: openAddModal,
                  }}
                >
                  <p>
                    Add a store by its myshopify domain to let it subscribe in
                    test mode without being charged.
                  </p>
                </EmptyState>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'text']}
                  headings={[
                    'Domain',
                    'Note',
                    'Added by',
                    'Added at',
                    'Actions',
                  ]}
                  rows={rows}
                />
              )}
            </Card>
          </Layout.Section>
        </Layout>

        {/* Add test store modal */}
        <Modal
          open={addModalOpen}
          onClose={closeAddModal}
          title="Add test store"
          primaryAction={{
            content: 'Add store',
            onAction: handleAddSubmit,
            loading: submitting,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: closeAddModal,
              disabled: submitting,
            },
          ]}
        >
          <Modal.Section>
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
            >
              <TextField
                label="Myshopify domain"
                value={domainInput}
                onChange={handleDomainChange}
                autoComplete="off"
                placeholder="my-store.myshopify.com"
                error={domainError}
                helpText="The store's permanent .myshopify.com domain."
              />
              <TextField
                label="Note"
                value={noteInput}
                onChange={setNoteInput}
                autoComplete="off"
                multiline={2}
                placeholder="Why is this store on the allowlist?"
              />
            </div>
          </Modal.Section>
        </Modal>

        {/* Remove confirmation modal */}
        <Modal
          open={removeTarget !== null}
          onClose={() => (removing ? undefined : setRemoveTarget(null))}
          title="Remove test store"
          primaryAction={{
            content: 'Remove',
            destructive: true,
            onAction: handleRemoveConfirm,
            loading: removing,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => setRemoveTarget(null),
              disabled: removing,
            },
          ]}
        >
          <Modal.Section>
            <Text as="p" variant="bodyMd">
              Remove{' '}
              <Text as="span" fontWeight="semibold">
                {removeTarget?.myshopify_domain}
              </Text>{' '}
              from the test-store allowlist? New subscriptions from this store
              will be charged normally.
            </Text>
          </Modal.Section>
        </Modal>
      </Page>
    </>
  )
}
